import React, { useState, useEffect } from 'react';
import MobileLayout from '../components/mobile/MobileLayout';
import { useDocuments } from '../hooks/useDocuments';
import { useChat } from '../hooks/useChat';

interface Message {
  id: string;
  type: 'question' | 'answer';
  content: string;
  timestamp: Date;
}

const MobilePDFAIChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentDocument, setCurrentDocument] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string>('');
  
  const { documents, loading: documentsLoading } = useDocuments();
  const { askQuestion } = useChat();

  // Set first document as current if available
  useEffect(() => {
    if (documents.length > 0 && !currentDocument) {
      const firstDoc = documents[0];
      setCurrentDocument(firstDoc.title);
      setPdfUrl(firstDoc.storage_url || '');
    }
  }, [documents, currentDocument]);

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      type: 'question',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    try {
      await askQuestion(message, documents.find(doc => doc.title === currentDocument)?.id);
      
      // Note: The actual response will be handled by the useChat hook
      // For now, we'll add a placeholder response
      const answerMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'answer',
        content: 'Processing your question...',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, answerMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'answer',
        content: 'Sorry, there was an error processing your request. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleGenerateSummary = async () => {
    const summaryMessage = "📊 Generating professional summary...";
    const newMessage: Message = {
      id: Date.now().toString(),
      type: 'question',
      content: summaryMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    try {
      await askQuestion("Generate a professional summary of this document", documents.find(doc => doc.title === currentDocument)?.id);
      
      const answerMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'answer',
        content: 'Summary generated successfully.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, answerMessage]);
    } catch (error) {
      console.error('Error generating summary:', error);
    }
  };

  const handleShowSuggestions = async () => {
    const suggestionsMessage = "❓ Here are some suggested questions:";
    const newMessage: Message = {
      id: Date.now().toString(),
      type: 'answer',
      content: suggestionsMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    // Add suggested questions
    const suggestions = [
      "What are the key findings in this report?",
      "What are the main recommendations?",
      "What are the risks mentioned?",
      "What is the market outlook?"
    ];

    suggestions.forEach((suggestion, index) => {
      setTimeout(() => {
        const suggestionMessage: Message = {
          id: (Date.now() + index).toString(),
          type: 'answer',
          content: `💡 ${suggestion}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, suggestionMessage]);
      }, index * 500);
    });
  };

  const handleViewPDF = () => {
    console.log('Opening PDF viewer');
  };

  const handleSearchDocument = async () => {
    const searchMessage = "🔍 What would you like to search for in the document?";
    const newMessage: Message = {
      id: Date.now().toString(),
      type: 'answer',
      content: searchMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
  };

  if (documentsLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading documents...
      </div>
    );
  }

  return (
    <MobileLayout
      currentDocument={currentDocument}
      messages={messages}
      onSendMessage={handleSendMessage}
      onGenerateSummary={handleGenerateSummary}
      onShowSuggestions={handleShowSuggestions}
      onViewPDF={handleViewPDF}
      onSearchDocument={handleSearchDocument}
      pdfUrl={pdfUrl}
    />
  );
};

export default MobilePDFAIChat; 